import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Receipt, GoogleLogo, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackConversion } from "@/lib/adsConversions";

export default function AuthPage({ mode }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("personal"); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const adding = params.get("add") === "1";
  const q = adding ? "?add=1" : "";

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        navigate(u.onboarded ? "/" : "/onboarding");
      } else if (mode === "register") {
        const u = await register(email, password, name);
        trackConversion("signup");
        navigate("/onboarding");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that account exists, a reset link is on its way to your inbox.");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: params.get("token"), password });
        toast.success("Password updated. Please log in.");
        navigate("/login");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const useDemo = async () => {
    setLoading(true);
    try {
      const u = await login("demo@sliply.app", "Demo@2026");
      navigate(u.onboarded ? "/" : "/onboarding");
    } catch {
      toast.error("Demo login failed");
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: adding ? "Add another account" : "Welcome back",
    register: adding ? "Add another account" : "Create your account",
    forgot: "Reset your password", reset: "Set a new password",
  };
  const subtitles = {
    login: adding ? "Sign into an existing account to add it to this device — switch anytime." : "Your AI financial memory for every purchase.",
    register: adding ? "Create a new account with a different email. Each account keeps its own receipts, budgets and Moolas." : "Turn receipts into a searchable spending database.",
    forgot: "Enter your email and we'll send you a reset link.",
    reset: "Choose a strong new password.",
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-riq-bg">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-riq-primary text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] riq-noise" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center p-2.5"><img src="/sliply-mark.png" alt="Sliply" className="w-full h-full object-contain" /></div>
          <span className="font-head text-xl font-bold">Sliply</span>
        </div>
        <div className="relative">
          <h1 className="font-head text-4xl xl:text-5xl font-bold tracking-tight leading-tight">Turn receipts into a searchable spending database.</h1>
          <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">Scan any receipt. AI extracts every item, tracks prices, spots patterns and answers questions about your money.</p>
          <div className="mt-10 flex flex-wrap gap-3 text-sm">
            {["Item-level tracking", "Purchase patterns", "AI assistant", "Smart budgets"].map((t) => (
              <span key={t} className="rounded-full bg-white/10 px-4 py-2">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative text-white/40 text-sm">Built for South Africa · ZAR</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-riq-primary flex items-center justify-center p-2"><img src="/sliply-mark.png" alt="Sliply" className="w-full h-full object-contain" /></div>
            <span className="font-head text-xl font-bold text-riq-primary">Sliply</span>
          </div>
          <h2 className="font-head text-3xl font-bold tracking-tight text-riq-primary">{titles[mode]}</h2>
          <p className="text-muted-foreground mt-2 mb-8">{subtitles[mode]}</p>

          {(mode === "login" || mode === "register") && (
            <>
              <button data-testid="google-login-btn" onClick={googleLogin} className="w-full flex items-center justify-center gap-3 rounded-full border border-riq-border bg-white py-3 font-medium hover:-translate-y-0.5 transition-transform">
                <GoogleLogo size={20} weight="bold" /> Continue with Google
              </button>
              <div className="flex items-center gap-4 my-6 text-xs text-muted-foreground">
                <div className="flex-1 h-px bg-riq-border" /> OR <div className="flex-1 h-px bg-riq-border" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Input testId="auth-name" label="Name" value={name} onChange={setName} placeholder="Your name" />
            )}
            {mode !== "reset" && (
              <Input testId="auth-email" label="Email" type="email" value={email} onChange={setEmail} placeholder="you@email.com" required />
            )}
            {mode !== "forgot" && (
              <Input testId="auth-password" label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" required />
            )}
            {mode === "login" && (
              <div className="text-right"><Link to="/forgot" className="text-sm text-riq-primary hover:underline">Forgot password?</Link></div>
            )}
            <button data-testid="auth-submit-btn" disabled={loading} type="submit" className="w-full flex items-center justify-center gap-2 rounded-full bg-riq-primary text-white py-3.5 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {loading ? <CircleNotch className="animate-spin" size={18} /> : <>{mode === "login" ? "Log in" : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : "Update password"} <ArrowRight size={18} weight="bold" /></>}
            </button>
          </form>

          {mode === "login" && (
            <button data-testid="demo-login-btn" onClick={useDemo} className="mt-4 w-full rounded-full border border-riq-border py-3 text-sm font-medium text-riq-primary hover:bg-secondary transition-colors">
              Explore the demo account
            </button>
          )}

          <div className="mt-8 text-center text-sm text-muted-foreground">
            {mode === "login" && <>New here? <Link to={`/register${q}`} className="text-riq-primary font-medium hover:underline">Create an account</Link></>}
            {mode === "register" && <>Already have an account? <Link to={`/login${q}`} className="text-riq-primary font-medium hover:underline">Log in</Link></>}
            {(mode === "forgot" || mode === "reset") && <Link to={`/login${q}`} className="text-riq-primary font-medium hover:underline">Back to login</Link>}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, required, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
      <input data-testid={testId} type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-secondary/60 rounded-xl border border-transparent focus:border-riq-primary focus:ring-2 focus:ring-riq-primary/20 px-4 py-3 text-sm outline-none" />
    </label>
  );
}
